import { NgOptimizedImage } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  Signal,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, switchMap } from 'rxjs';
import {
  BreadcrumbComponent,
  BreadcrumbItem,
} from '../../../../shared/components/breadcrumb/breadcrumb.component';
import { LucideModule } from '../../../../shared/modules/lucide.module';
import { AlertaService } from '../../../../shared/utils/alerta.service';
import { EtapasServicoComponent } from '../../pages-servicos/etapas-servico/etapas-servico.component';
import { EtapaConfirmacaoOuvidoriaComponent } from './etapa-confirmacao-ouvidoria/etapa-confirmacao-ouvidoria.component';
import { EtapaInformacoesOuvidoriaComponent } from './etapa-informacoes-ouvidoria/etapa-informacoes-ouvidoria.component';
import { EtapaPrazoOuvidoriaComponent } from './etapa-prazo-ouvidoria/etapa-prazo-ouvidoria.component';
import { EtapaSolicitacaoOuvidoriaComponent } from './etapa-solicitacao-ouvidoria/etapa-solicitacao-ouvidoria.component';
import { ManifestacaoStore } from './manifestacao.store';
import { TIPO_POR_ROTA } from '../models/manifestacao-publica.model';
import { mapearErro, OuvidoriaPublicaService } from '../services/ouvidoria-publica.service';
import { ScrollNavegacaoService } from '../../../../core/services/scroll-navegacao.service';

interface TipoManifestacao {
  titulo: string;
  /** Nome no singular usado nos botões de enviar e cancelar. */
  rotulo: string;
  icone: string;
  descricao: string;
  rotuloDescricao: string;
  placeholderDescricao: string;
  banner: string;
  bannerLargura: number;
  bannerAltura: number;
}

const BANNERS = 'assets/images/ouvidoria';

const TRILHA: readonly BreadcrumbItem[] = [
  { titulo: 'Ouvidoria geral do Município', link: '/ouvidoria' },
];

const ETAPAS: readonly string[] = ['Informações', 'Solicitação', 'Prazo', 'Confirmação'];

/** Índices das etapas, para não espalhar números soltos pelo fluxo. */
const SOLICITACAO = 1;
const PRAZO = 2;
const CONFIRMACAO = 3;

const PADRAO: TipoManifestacao = {
  titulo: 'Manifestação',
  rotulo: 'Manifestação',
  icone: 'megaphone',
  descricao:
    'A Ouvidoria da Prefeitura de Salvador recebe manifestações dos cidadãos sobre serviços públicos ' +
    'municipais. Toda manifestação é registrada, acompanhada e respondida dentro dos prazos legais ' +
    'estabelecidos.',
  rotuloDescricao: 'Descrição da manifestação',
  placeholderDescricao: 'Descreva sua manifestação...',
  banner: `${BANNERS}/ouvidoria-reclamacao.png`,
  bannerLargura: 2172,
  bannerAltura: 724,
};

const TIPOS: Record<string, TipoManifestacao> = {
  reclamacao: {
    titulo: 'Reclamação',
    rotulo: 'Reclamação',
    icone: 'flag',
    descricao:
      'A Ouvidoria da Prefeitura de Salvador recebe reclamações dos cidadãos sobre serviços públicos ' +
      'municipais, irregularidades e condutas inadequadas de agentes públicos. Toda manifestação é ' +
      'registrada, acompanhada e respondida dentro dos prazos legais estabelecidos.',
    rotuloDescricao: 'Descrição da reclamação',
    placeholderDescricao: 'Descreva sua reclamação...',
    banner: `${BANNERS}/ouvidoria-reclamacao.png`,
    bannerLargura: 2172,
    bannerAltura: 724,
  },
  elogios: {
    titulo: 'Elogios',
    rotulo: 'Elogio',
    icone: 'thumbs-up',
    descricao:
      'A Ouvidoria da Prefeitura de Salvador recebe elogios dos cidadãos sobre serviços públicos ' +
      'municipais e sobre o atendimento prestado por agentes públicos. Todo elogio é registrado e ' +
      'encaminhado à equipe responsável.',
    rotuloDescricao: 'Descrição do elogio',
    placeholderDescricao: 'Descreva seu elogio...',
    banner: `${BANNERS}/ouvidoria-elogio.png`,
    bannerLargura: 2172,
    bannerAltura: 724,
  },
  sugestoes: {
    titulo: 'Sugestões',
    rotulo: 'Sugestão',
    icone: 'lightbulb',
    descricao:
      'A Ouvidoria da Prefeitura de Salvador recebe sugestões dos cidadãos para aprimorar os serviços ' +
      'públicos municipais. Toda sugestão é registrada, analisada e respondida dentro dos prazos legais ' +
      'estabelecidos.',
    rotuloDescricao: 'Descrição da sugestão',
    placeholderDescricao: 'Descreva sua sugestão...',
    banner: `${BANNERS}/ouvidoria-sugestao.png`,
    bannerLargura: 2172,
    bannerAltura: 724,
  },
  solicitacao: {
    titulo: 'Solicitação',
    rotulo: 'Solicitação',
    icone: 'clipboard-list',
    descricao:
      'A Ouvidoria da Prefeitura de Salvador recebe solicitações de providências e de serviços públicos ' +
      'municipais. Toda solicitação é registrada, encaminhada ao órgão responsável e respondida dentro ' +
      'dos prazos legais estabelecidos.',
    rotuloDescricao: 'Descrição da solicitação',
    placeholderDescricao: 'Descreva sua solicitação...',
    banner: `${BANNERS}/ouvidoria-servico.png`,
    bannerLargura: 1916,
    bannerAltura: 821,
  },
  denuncia: {
    titulo: 'Denúncia',
    rotulo: 'Denúncia',
    icone: 'triangle-alert',
    descricao:
      'A Ouvidoria da Prefeitura de Salvador recebe denúncias sobre irregularidades na prestação de ' +
      'serviços públicos municipais e sobre condutas inadequadas de agentes públicos. O sigilo do ' +
      'denunciante é garantido nos termos da lei.',
    rotuloDescricao: 'Descrição da denúncia',
    placeholderDescricao: 'Descreva sua denúncia...',
    banner: `${BANNERS}/ouvidoria-denuncia.png`,
    bannerLargura: 2172,
    bannerAltura: 724,
  },
};

@Component({
  selector: 'app-manifestacao',
  templateUrl: './manifestacao.component.html',
  styleUrls: ['./manifestacao.component.css'],
  imports: [
    NgOptimizedImage,
    BreadcrumbComponent,
    LucideModule,
    EtapasServicoComponent,
    EtapaInformacoesOuvidoriaComponent,
    EtapaSolicitacaoOuvidoriaComponent,
    EtapaPrazoOuvidoriaComponent,
    EtapaConfirmacaoOuvidoriaComponent,
  ],
  providers: [ManifestacaoStore],
})
export class ManifestacaoComponent {
  private readonly alerta = inject(AlertaService);
  private readonly scrollNavegacao = inject(ScrollNavegacaoService);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly store = inject(ManifestacaoStore);
  protected readonly ouvidoria = inject(OuvidoriaPublicaService);

  readonly tipo = input('');

  readonly trilha = TRILHA;
  readonly etapas = ETAPAS;

  readonly etapaAtual = signal(0);
  /** A etapa de solicitação só é liberada após login, acesso gov.br ou escolha anônima. */
  readonly etapaLiberada = signal(0);

  /** Elemento da trilha de etapas: é por ele que a etapa recém-aberta começa na tela. */
  private readonly trilhaEtapas: Signal<ElementRef<HTMLElement> | undefined> = viewChild(
    'trilhaEtapas',
    { read: ElementRef },
  );

  /** Contador de pedidos de foco no card de acesso; cada incremento leva o cursor até ele. */
  readonly focarLogin = signal(0);

  readonly etapaPrazo = PRAZO;

  readonly etapaConfirmacao = CONFIRMACAO;

  readonly manifestacao = computed<TipoManifestacao>(() => TIPOS[this.tipo()] ?? PADRAO);

  /** A chave que a API entende, traduzida da rota. Rota fora do catálogo não envia. */
  readonly tipoApi = computed(() => TIPO_POR_ROTA[this.tipo()]);

  /**
   * A etapa de Solicitação só é liberada com canal `Ativo` carregado: sem ele o envio não teria
   * como dar certo, e deixar o cidadão preencher tudo para falhar no fim é pior do que avisar já.
   */
  readonly podeLiberarSolicitacao = computed(
    () => this.ouvidoria.estadoCanal() === 'pronto' && !!this.tipoApi(),
  );

  /** Envio em curso: desabilita o botão e é o que impede o segundo POST do duplo clique. */
  readonly enviando = signal(false);

  /** Sem anonimato habilitado no canal, a mensagem do stepper não pode oferecê-lo. */
  readonly mensagemBloqueio = computed(() => {
    if (this.etapaLiberada() > 0) return 'Conclua a etapa anterior para liberar';

    return this.ouvidoria.permiteAnonimato()
      ? 'Entre com login, gov.br ou escolha solicitar anonimamente para liberar'
      : 'Entre com login ou gov.br para liberar';
  });

  private readonly formulario = viewChild(EtapaSolicitacaoOuvidoriaComponent);

  readonly podeAvancar = computed(() => {
    if (this.etapaAtual() !== SOLICITACAO) return true;

    return this.store.valido();
  });

  constructor() {
    // O canal governa a tela inteira (anonimato, sigilo, anexo, prazo) e é buscado uma vez por
    // sessão — trocar de `ouvidoria/reclamacao` para `ouvidoria/elogios` reaproveita o cache.
    this.ouvidoria.carregarCanal();

    // O desafio de formulário é pedido junto do canal: ele envelhece enquanto o cidadão
    // preenche, e chega ao envio já com a idade mínima que a API exige.
    this.store.desafio.carregar();

    let primeiraEtapa = true;

    // Trocar de etapa é uma ação de navegação: a nova etapa abre a partir do seu topo,
    // sem rolagem animada. A abertura da página fica de fora — ali o topo é o da rota.
    effect(() => {
      this.etapaAtual();

      if (primeiraEtapa) {
        primeiraEtapa = false;
        return;
      }

      afterNextRender(() => this.rolarParaEtapa(), { injector: this.injector });
    });
  }

  private rolarParaEtapa(): void {
    this.scrollNavegacao.aoInicioDaEtapa(this.trilhaEtapas()?.nativeElement);
  }

  irParaEtapa(indice: number): void {
    if (indice > this.etapaLiberada()) return;

    this.etapaAtual.set(indice);
  }

  avancar(): void {
    if (!this.podeAvancar()) {
      this.formulario()?.destacarPendencias();
      return;
    }

    const proxima = Math.min(this.etapaAtual() + 1, this.etapas.length - 1);

    this.etapaLiberada.update((liberada) => Math.max(liberada, proxima));
    this.etapaAtual.set(proxima);
  }

  /**
   * Confirma antes de registrar e só então chama a API. A Confirmação abre **somente** com o
   * 201 — o protocolo que ela mostra é o que o back-end gerou, não um número de exemplo.
   */
  async enviar(): Promise<void> {
    const tipo = this.tipoApi();

    if (!tipo || this.enviando()) return;

    const rotulo = this.manifestacao().rotulo.toLowerCase();

    const confirmado = await this.alerta.confirmar({
      titulo: `Enviar ${rotulo}?`,
      texto:
        `A ${rotulo} será registrada na Ouvidoria e receberá um número de protocolo. Depois do ` +
        'envio o conteúdo não pode mais ser alterado.',
      confirmar: 'Sim, enviar',
      cancelar: 'Revisar antes',
      tom: 'info',
    });

    if (!confirmado) return;

    this.enviando.set(true);
    this.store.erroEnvio.set(null);

    const payload = this.store.paraPayload(tipo);
    const anexos = this.store.arquivosParaEnvio();

    // Se a carga do desafio falhou, `paraEnvio` pede outro agora; se falhar de novo, cai no
    // erro de rede de sempre.
    this.store.desafio
      .paraEnvio()
      .pipe(
        switchMap((desafio) => this.ouvidoria.abrir(payload, anexos, desafio, this.store.desafio.honeypot())),
        finalize(() => this.enviando.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (resposta) => {
          this.store.respostaAbertura.set(resposta);
          this.etapaLiberada.set(CONFIRMACAO);
          this.etapaAtual.set(CONFIRMACAO);
        },
        error: (erro: unknown) => this.tratarErroDeEnvio(erro),
      });
  }

  /**
   * Cada tipo de erro tem a etapa onde o cidadão pode fazer algo a respeito: o 400 é sobre o que
   * ele preencheu, o 404 é sobre o canal ter saído do ar, e limite/indisponibilidade são só
   * esperar — nesses ele fica onde está. Nada é desliberado e nenhum dado é perdido.
   */
  private tratarErroDeEnvio(erro: unknown): void {
    const mapeado = mapearErro(erro);

    this.store.erroEnvio.set(mapeado);

    // O desafio foi recusado (vencido, reusado, cedo demais): pede outro e o cidadão envia de
    // novo da etapa Prazo, com os dados e os anexos preservados.
    if (mapeado.tipo === 'desafio') {
      this.store.desafio.renovar();
      return;
    }

    if (mapeado.tipo === 'validacao' || mapeado.tipo === 'nao-encontrado') {
      this.etapaAtual.set(SOLICITACAO);
      return;
    }

    if (mapeado.tipo === 'sem-canal') {
      this.ouvidoria.marcarSemCanal();
      this.etapaAtual.set(0);
    }

    // 'limite' e 'indisponivel' ficam na etapa Prazo, com a mensagem inline — como o 'desafio'.
  }

  voltar(): void {
    this.etapaAtual.update((etapa) => Math.max(etapa - 1, 0));
  }

  solicitar(): void {
    if (!this.podeLiberarSolicitacao()) return;

    this.store.entrarIdentificado();
    this.abrirSolicitacao();
  }

  /** Sem identificação: a etapa não pede os dados pessoais. */
  seguirAnonimo(): void {
    if (!this.podeLiberarSolicitacao() || !this.ouvidoria.permiteAnonimato()) return;

    this.store.entrarAnonimo();
    this.abrirSolicitacao();
  }

  private abrirSolicitacao(): void {
    this.etapaLiberada.update((liberada) => Math.max(liberada, SOLICITACAO));
    this.etapaAtual.set(SOLICITACAO);
  }

  autenticar(): void {
    this.store.sessaoIniciada.set(true);
    this.solicitar();
  }

  /** "Tentar novamente" do aviso de canal indisponível na etapa de Informações. */
  recarregarCanal(): void {
    this.ouvidoria.recarregarCanal();
  }

  /** O "Enviar" do topo leva ao card de acesso: a etapa só é liberada pela escolha feita lá. */
  irParaLogin(): void {
    this.etapaAtual.set(0);
    this.focarLogin.update((vezes) => vezes + 1);
  }

  encerrarSessao(): void {
    this.reiniciar();
  }

  async cancelar(): Promise<void> {
    const confirmado = await this.alerta.confirmar({
      titulo: `Cancelar ${this.manifestacao().titulo.toLowerCase()}?`,
      texto: 'Os dados preenchidos até aqui serão descartados.',
      confirmar: 'Sim, cancelar',
      cancelar: 'Continuar preenchendo',
    });

    if (confirmado) {
      this.reiniciar();
    }
  }

  reiniciar(): void {
    this.store.limpar();
    this.etapaAtual.set(0);
    this.etapaLiberada.set(0);
  }
}
